const OBSERVER_WATCHDOG_MS = 4000;

class ProductRecommendationsLoader extends HTMLElement {
  #preloadObserver;
  #purchaseObserver;
  #analyticsObserver;
  #observerWatchdog;
  #loading;
  #purchaseControlsPromise;
  #purchaseControlsVisible = false;
  #impressedProductIds = new Set();

  connectedCallback() {
    this.#disconnectObservers();
    this.#clearObserverWatchdog();
    this.removeEventListener('click', this.#handleRecommendationClick);
    this.addEventListener('click', this.#handleRecommendationClick);

    const observersReady = this.#setupVisibilityObservers();

    if (!observersReady) {
      // Compatibility path for older or restricted webviews: load immediately
      // rather than leaving server-rendered skeletons waiting on an API that
      // does not exist or cannot be constructed.
      this.#purchaseControlsVisible = true;
      void this.#load();
    } else {
      // Some in-app browsers expose IntersectionObserver but never deliver a
      // callback. The watchdog preserves lazy loading normally, then forces a
      // deterministic load if the implementation is silently broken.
      this.#observerWatchdog = window.setTimeout(() => {
        this.#preloadObserver?.disconnect();
        this.#purchaseObserver?.disconnect();
        this.#purchaseControlsVisible = true;
        void this.#load();
      }, OBSERVER_WATCHDOG_MS);
    }

    this.#bindRecommendationAnalytics();
  }

  disconnectedCallback() {
    this.#disconnectObservers();
    this.#clearObserverWatchdog();
    this.removeEventListener('click', this.#handleRecommendationClick);
  }

  #setupVisibilityObservers() {
    if (typeof window.IntersectionObserver !== 'function') return false;

    try {
      // Preload recommendation content before it reaches the viewport, but
      // keep purchase components dormant until the section is actually visible.
      this.#preloadObserver = new window.IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          void this.#load();
        },
        { rootMargin: '900px 0px' }
      );

      this.#purchaseObserver = new window.IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#purchaseControlsVisible = true;
          observer.disconnect();
          void this.#activatePurchaseControls().catch(() => {});
        },
        { rootMargin: '0px' }
      );

      this.#preloadObserver.observe(this);
      this.#purchaseObserver.observe(this);
      return true;
    } catch (error) {
      this.#disconnectObservers();
      console.warn('Product recommendations observer unavailable; loading immediately.', error);
      return false;
    }
  }

  #disconnectObservers() {
    this.#preloadObserver?.disconnect();
    this.#purchaseObserver?.disconnect();
    this.#analyticsObserver?.disconnect();
    this.#preloadObserver = undefined;
    this.#purchaseObserver = undefined;
    this.#analyticsObserver = undefined;
  }

  #clearObserverWatchdog() {
    if (this.#observerWatchdog === undefined) return;
    window.clearTimeout(this.#observerWatchdog);
    this.#observerWatchdog = undefined;
  }

  #handleRecommendationClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const item = link.closest('.resource-list__item');
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;

    const productId = this.#getRecommendedProductId(item);
    if (!productId) return;

    this.#publishRecommendationEvent('velzeen_recommendation_click', item, link.href);
  };

  #getRecommendedProductId(item) {
    return (
      item.dataset.recommendedProductId ||
      item.querySelector('product-card[data-product-id]')?.dataset.productId ||
      ''
    );
  }

  #getRecommendationPosition(item, productId) {
    const explicitPosition = Number.parseInt(item.dataset.recommendationPosition || '', 10);
    if (Number.isFinite(explicitPosition) && explicitPosition > 0) return explicitPosition;

    const uniqueProductIds = [];
    this.querySelectorAll('.resource-list__item').forEach((candidate) => {
      if (!(candidate instanceof HTMLElement)) return;
      const candidateId = this.#getRecommendedProductId(candidate);
      if (candidateId && !uniqueProductIds.includes(candidateId)) uniqueProductIds.push(candidateId);
    });

    const index = uniqueProductIds.indexOf(productId);
    return index >= 0 ? index + 1 : null;
  }

  #publishRecommendationEvent(eventName, item, destinationUrl = '') {
    const recommendedProductId = this.#getRecommendedProductId(item);
    if (!recommendedProductId) return;

    const fallbackLink = item.querySelector('a[href]');
    const payload = {
      source_product_id: this.dataset.productId || '',
      source_product_handle: this.dataset.sourceProductHandle || '',
      recommended_product_id: recommendedProductId,
      recommendation_position: this.#getRecommendationPosition(item, recommendedProductId),
      recommendation_intent: this.dataset.intent || 'related',
      section_id: this.dataset.sectionId || '',
      destination_url:
        destinationUrl || (fallbackLink instanceof HTMLAnchorElement ? fallbackLink.href : ''),
    };

    document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));

    try {
      const publishResult = window.Shopify?.analytics?.publish?.(eventName, payload);
      if (publishResult && typeof publishResult.catch === 'function') {
        publishResult.catch(() => {});
      }
    } catch {
      // Analytics must never interrupt recommendation navigation.
    }
  }

  #bindRecommendationAnalytics() {
    this.#analyticsObserver?.disconnect();
    this.#analyticsObserver = undefined;

    const items = [...this.querySelectorAll('.resource-list__item')].filter(
      (item) => item instanceof HTMLElement && this.#getRecommendedProductId(item)
    );
    if (!items.length || typeof window.IntersectionObserver !== 'function') return;

    try {
      this.#analyticsObserver = new window.IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
            const item = entry.target;
            if (!(item instanceof HTMLElement)) return;

            const productId = this.#getRecommendedProductId(item);
            if (!productId || this.#impressedProductIds.has(productId)) {
              observer.unobserve(item);
              return;
            }

            this.#impressedProductIds.add(productId);
            this.#publishRecommendationEvent('velzeen_recommendation_impression', item);
            observer.unobserve(item);
          });
        },
        { threshold: 0.5 }
      );

      items.forEach((item) => this.#analyticsObserver.observe(item));
    } catch {
      // Recommendation analytics is optional and must never block rendering.
      this.#analyticsObserver?.disconnect();
      this.#analyticsObserver = undefined;
    }
  }

  async #activatePurchaseControls() {
    if (!this.#purchaseControlsVisible) return;

    if (!this.#purchaseControlsPromise) {
      this.#purchaseControlsPromise = (async () => {
        if (document.getElementById('quick-add-dialog')) {
          await import('./quick-add.js');
        }
      })();
    }

    await this.#purchaseControlsPromise;
    this.dataset.purchaseControlsReady = 'true';
    this.dispatchEvent(new CustomEvent('product-recommendations:ready', { bubbles: true }));
  }

  #finishLoading(state) {
    this.#clearObserverWatchdog();
    this.removeAttribute('aria-busy');
    this.dataset.loadingState = state;

    const skeletons = [...this.querySelectorAll('.product-recommendations__skeleton-item')];
    const skeletonContainers = new Set(
      skeletons
        .map((skeleton) => skeleton.closest('.resource-list'))
        .filter((container) => container instanceof HTMLElement)
    );

    skeletons.forEach((skeleton) => skeleton.remove());
    skeletonContainers.forEach((container) => {
      if (container.children.length === 0 && container.textContent.trim() === '') container.remove();
    });
  }

  async #load() {
    if (this.#loading) return this.#loading;

    this.setAttribute('aria-busy', 'true');
    this.dataset.loadingState = 'loading';

    this.#loading = (async () => {
      let outcome = 'loaded';

      try {
        const [runtime] = await Promise.all([
          import('./product-recommendations-runtime.js'),
          import('./product-card.js'),
          import('./product-title-truncation.js'),
          import('./show-more.js'),
        ]);

        await runtime.hydrateProductRecommendations(this);
        this.#bindRecommendationAnalytics();

        // Hydration can add new purchase roots after the visibility event. Emit
        // readiness again so those roots initialize only when the section is visible.
        if (this.#purchaseControlsVisible) await this.#activatePurchaseControls();
      } catch (error) {
        if (error?.name === 'EmptyRecommendationsError') {
          outcome = 'empty';
        } else if (error?.name === 'TimeoutError') {
          outcome = 'timeout';
        } else {
          outcome = 'error';
        }

        console.error('Product recommendations error:', error);
        this.classList.add('hidden');
        this.dataset.error = error instanceof Error ? error.message : 'Error loading product recommendations';
      } finally {
        this.#finishLoading(outcome);
      }
    })();

    return this.#loading;
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendationsLoader);
}
